import {Table, Button, message, Row, Col, Modal, Space } from "antd";
import { mainColumns, nestedColumns } from "./PlaidDetailsTableColumns";
import { useEffect, useState } from "react";
import LoaderComponent from "../../../Loader";
import API from "../../../../services/API";
import CustomText from "../../../CustomText";
import { convertCentsIntoDollar, isSupportLead, isSupportAgent2, isSupportAgent3 } from "../../../../helpers/genericHelper";
import { PLAID_ACCOUNT_STATE } from "../../../../constants/AppConstants";
import PlaidBalance from "./PlaidBalance";
import PlaidEvents from "./PlaidEvents";
import PlaidDetailsRow from "./PlaidDetailsRow";
import usePageState from "../../../../helpers/usePageState";

export default function PlaidDetails({ userId, refreshAccount }) {
  const [plaidAccounts, setPlaidAccounts] = useState([]);
  const [plaidLinkAllowed, setPlaidLinkAllowed] = useState(null);
  const [changeActivePlaidModalVisible, setChangeActivePlaidModalVisible] = useState(false);
  const [changeDeletePlaidModalVisible, setChangeDeletePlaidModalVisible] = useState(false);
  const [hidePlaidModalVisible, setHidePlaidModalVisible] = useState(false);
  const [selectedPlaidId, setSelectedPlaidId] = useState(null);
  const [selectedPlaidIsDeleted, setSelectedPlaidIsDeleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [plaidBalances, setPlaidBalances] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = usePageState("plaid-accounts-rows", ["0"]);

  const successMessage = (content, duration = 5) => {
    message.success(content, duration);
  };

  const errorMessage = (content, duration = 10) => {
    message.error(content, duration);
  };

  const nestedColumnsWithFormattedSilaFailReason = nestedColumns.map((column) => {
    if (column.dataIndex === "silaFailReason") {
      return {
        ...column,
        render: (failReasons) => (
          <p style={{ whiteSpace: "pre-line" }}>{failReasons}</p>
        ),
      };
    }
    return column;
  });

  const showChangeActivePlaidModal = (id) => {
    setSelectedPlaidId(id);
    setChangeActivePlaidModalVisible(true);
  };

  const closeChangeActivePlaidModal = () => {
    setChangeActivePlaidModalVisible(false);
  }

  const showChangeDeletePlaidModal = (id) => {
    setSelectedPlaidId(id);
    setChangeDeletePlaidModalVisible(true);
  };

  const closeChangeDeletePlaidModal = () => {
    setChangeDeletePlaidModalVisible(false);
  }

  const showHidePlaidModal = (id, isDeleted) => {
    setSelectedPlaidId(id);
    setSelectedPlaidIsDeleted(isDeleted);
    setHidePlaidModalVisible(true);
  };

  const closeHidePlaidModal = () => {
    setHidePlaidModalVisible(false);
  }

  const changeActivePlaid = async () => {
    try {
      await API.changeActivePlaidAccount(userId, selectedPlaidId);
      message.success("Successfully set selected plaid account as active.")
      refreshAccount();
    } catch (ex) {
      errorMessage("Unable to set this plaid account to active.");
      errorMessage(ex?.response?.data?.error_message || ex?.response?.data?.errorMessage);
    } finally {
      setChangeActivePlaidModalVisible(false);
    }
  };

  const deletePlaidAccount = async () => {
    try {
      await API.deletePlaidAccount({ userId, plaidAccountId: selectedPlaidId });
      message.success("Successfully deleted plaid account.")
      refreshAccount();
    } catch (ex) {
      errorMessage("Unable to delete plaid account.");
      errorMessage(ex?.response?.data?.error_message || ex?.response?.data?.errorMessage);
    } finally {
      setChangeDeletePlaidModalVisible(false);
    }
  };

  const hidePlaidAccount = async () => {
    try {
      await API.hidePlaidAccount({ userId, plaidAccountId: selectedPlaidId });
      message.success("Successfully hid plaid account from user.");
      refreshAccount();
    } catch (ex) {
      errorMessage("Unable to hide plaid account.");
      errorMessage(ex?.response?.data?.error_message || ex?.response?.data?.errorMessage);
    } finally {
      setHidePlaidModalVisible(false);
    }
  };

  const checkBalance = async (account) => {
    setBalanceLoading(true);
    try {
      let res = await API.getUserBalance({ userId, accountId: account.id });
      message.success(`Tha balance on the account (ending ${account.last4}) is ${convertCentsIntoDollar(res.balanceInCents)}`);
      balanceEntryPanel();
    } catch (ex) {
      errorMessage("Unable to delete plaid account.");
      errorMessage(ex?.response?.data?.error_message || ex?.response?.data?.errorMessage);
    } finally {
      setBalanceLoading(false);
    }
  };

  const expandedRowRender = (row) => {
    return <PlaidDetailsRow details={row} userId={userId} />
  };

  const plaidAccountDetails = async () => {
    try {
      setLoading(true);
      let res = await API.getPlaidAccountDetails(userId);
      const accountsWithKey = res.plaidConnections.map((account) => ({
          ...account,
          key: account.id,
      }));
      setPlaidAccounts(accountsWithKey);
      setPlaidLinkAllowed(res.plaidLinkAllowed);
    } catch (error) {
      errorMessage("Unable to fetch Plaid Accounts");
      errorMessage(error?.response?.data?.error_message || error?.response?.data?.errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const balanceEntryPanel = async () => {
    try {
      let res = await API.plaidBalances(userId);
      setPlaidBalances(res.plaidBalances);
    } catch (ex) {
      // handle error
    }
  };

  useEffect(() => {
      plaidAccountDetails();
      balanceEntryPanel();
  }, [userId]);

  useEffect(() => {
      if (plaidAccounts.length > 0 && plaidBalances.length > 0) {
          const accountMap = {};
          plaidAccounts.forEach(acc => {
              accountMap[acc.id] = acc.bankName;
          });
          setPlaidBalances(prevBalances =>
              prevBalances.map(balance => ({
                  ...balance,
                  bankName: accountMap[balance.plaidId] || 'Unknown'
              }))
          );
      }
  }, [plaidAccounts, plaidBalances]);

  return (
    <>
      {loading ? (
        <LoaderComponent />
      ) : (
        <>
          <>
            {plaidLinkAllowed !== 'null' && (
                <Row className="subscription-cancelled-card" gutter={[30, 30]}>
                    <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                        <h4>No Further Plaid Links Allowed</h4>
                        <CustomText dataSet={{ plaidLinkAllowed }} property1="plaidLinkAllowed" />
                    </Col>
                </Row>
            )}

            <Table
                columns={[
                    ...mainColumns,
                    {
                        title: "Actions",
                        key: "actions",
                        render: (text, record) => (
                            <>
                              <Space direction="vertical">
                                {isSupportAgent2() && record.accountState !== PLAID_ACCOUNT_STATE.EXPIRED
                                    && record.accountState !== PLAID_ACCOUNT_STATE.DELETED
                                    && record.accountState !== PLAID_ACCOUNT_STATE.REJECTED && (
                                    <Button
                                        type="primary"
                                        size="small"
                                        onClick={() => showChangeActivePlaidModal(record.id)}
                                        block
                                    >
                                        {'Override Bank Checks'}
                                    </Button>
                                )}

                                {isSupportAgent2() && record.accountState !== PLAID_ACCOUNT_STATE.DELETED
                                    && record.accountState !== PLAID_ACCOUNT_STATE.REJECTED && (
                                    <Button
                                        danger
                                        size="small"
                                        onClick={() => showChangeDeletePlaidModal(record.id)}
                                        block
                                    >
                                        {'Unlink'}
                                    </Button>
                                )}

                                {isSupportLead() && record.accountState !== PLAID_ACCOUNT_STATE.EXPIRED
                                    && record.accountState !== PLAID_ACCOUNT_STATE.DELETED
                                    && record.accountState !== PLAID_ACCOUNT_STATE.REJECTED && (
                                    <Button
                                      ghost
                                      type="primary"
                                      size="small"
                                      loading={balanceLoading}
                                      onClick={() => checkBalance(record)}
                                      block
                                    >
                                        {'Check Balance'}
                                    </Button>
                                )}

                                {isSupportAgent2() && (
                                    <Button
                                        danger
                                        size="small"
                                        onClick={() => showHidePlaidModal(record.id, record.accountState === PLAID_ACCOUNT_STATE.DELETED)}
                                        disabled={record.isHidden}
                                        block
                                    >
                                        {record.isHidden ? 'Hidden' : 'Hide in App'}
                                    </Button>
                                )}
                              </Space>
                            </>
                        )
                    }
                ]}
                expandable={{
                    expandedRowRender,
                    expandedRowKeys,
                    onExpandedRowsChange: setExpandedRowKeys,
                }}
                dataSource={plaidAccounts}
                pagination={false}
                loading={loading}
                rowClassName={(record) => (record.isActive ? "active-row" : "")}
            />
          </>
        </>
      )}

      <Modal
          title={"Confirmation"}
          centered
          mask={true}
          maskClosable={true}
          destroyOnClose={true}
          closable={true}
          open={changeActivePlaidModalVisible}
          onOk={changeActivePlaid}
          onCancel={closeChangeActivePlaidModal}
          className="confirm-modal"
      >
        <p>Are you sure you want to set this plaid account as active?</p>
      </Modal>
      
      <Modal
          title={"Confirmation"}
          centered
          mask={true}
          maskClosable={true}
          destroyOnClose={true}
          closable={true}
          open={changeDeletePlaidModalVisible}
          onOk={deletePlaidAccount}
          onCancel={closeChangeDeletePlaidModal}
          className="confirm-modal"
      >
        <p>Are you sure you want to unlink this plaid account?</p>
      </Modal>

      <Modal
          title={"Confirmation"}
          centered
          mask={true}
          maskClosable={true}
          destroyOnClose={true}
          closable={true}
          open={hidePlaidModalVisible}
          onOk={hidePlaidAccount}
          onCancel={closeHidePlaidModal}
          className="confirm-modal"
      >
        <p>Are you sure you want to hide this plaid account from the user?</p>
        {!selectedPlaidIsDeleted && (
          <p><strong>Warning:</strong> This will also unlink the account if it is not already unlinked.</p>
        )}
      </Modal>
      <br/>
      <PlaidEvents />
      <PlaidBalance plaidBalances={plaidBalances} />

    </>
  );
}